/**
 * APR trade-off baselines: acceptance rate and savings by APR.
 * Used to show Operators the trade-off between higher discount (more savings per invoice)
 * and lower supplier acceptance (fewer invoices accelerated).
 *
 * Data source: industry-style baselines. Later: replace with learned data by user and
 * by industry (e.g. from accept/decline history and optional industry segment).
 */

/** Baseline acceptance rate (0–100) by APR. Higher APR → typically lower acceptance. */
const ACCEPTANCE_RATE_BY_APR: { apr: number; rate: number }[] = [
  { apr: 5, rate: 98 },
  { apr: 8, rate: 95 },
  { apr: 10, rate: 92 },
  { apr: 12, rate: 88 },
  { apr: 13, rate: 85 },
  { apr: 15, rate: 78 },
  { apr: 17, rate: 62 },
  { apr: 18, rate: 52 },
  { apr: 20, rate: 38 },
  { apr: 25, rate: 18 },
  { apr: 30, rate: 10 },
  { apr: 40, rate: 4 },
];

/** Default days early for savings estimate when not provided */
const DEFAULT_DAYS_EARLY = 25;

/**
 * Estimated supplier acceptance rate at a given APR (baseline).
 * Linear interpolation between known points; clamped outside range.
 */
export function getBaselineAcceptanceRate(apr: number): number {
  const sorted = [...ACCEPTANCE_RATE_BY_APR].sort((a, b) => a.apr - b.apr);
  if (apr <= sorted[0].apr) return sorted[0].rate;
  if (apr >= sorted[sorted.length - 1].apr) return sorted[sorted.length - 1].rate;
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (apr >= a.apr && apr <= b.apr) {
      const t = (apr - a.apr) / (b.apr - a.apr);
      return Math.round(a.rate + t * (b.rate - a.rate));
    }
  }
  return sorted[sorted.length - 1].rate;
}

/**
 * Savings per $1,000 invoice for a given APR and days paid early.
 * Formula: 1000 * (apr/100) * (daysEarly/365)
 */
export function getSavingsPer1000(apr: number, daysEarly: number = DEFAULT_DAYS_EARLY): number {
  return (1000 * (apr / 100) * daysEarly) / 365;
}

/**
 * At a given APR, estimated total savings on a pipeline of invoices
 * if baseline acceptance applies: pipelineAmount * (apr/100) * (daysEarly/365) * (acceptRate/100)
 */
export function getEstimatedPipelineSavings(
  apr: number,
  pipelineAmount: number,
  daysEarly: number = DEFAULT_DAYS_EARLY
): number {
  const acceptRate = getBaselineAcceptanceRate(apr) / 100;
  return pipelineAmount * (apr / 100) * (daysEarly / 365) * acceptRate;
}

/**
 * Estimated number of invoices that would accept (baseline) given total count.
 */
export function getEstimatedInvoicesAccepted(apr: number, totalOffered: number): number {
  const rate = getBaselineAcceptanceRate(apr) / 100;
  return Math.round(totalOffered * rate);
}

export interface APRTradeoffSnapshot {
  apr: number;
  acceptanceRatePct: number;
  savingsPer10k: number;
  estimatedInvoicesAccepted: number;
  estimatedPipelineSavings: number;
}

export function getTradeoffSnapshot(
  apr: number,
  pipelineInvoiceCount: number,
  pipelineTotalAmount: number,
  daysEarly: number = DEFAULT_DAYS_EARLY
): APRTradeoffSnapshot {
  const acceptanceRatePct = getBaselineAcceptanceRate(apr);
  const savingsPer10k = getSavingsPer1000(apr, daysEarly) * 10;
  const estimatedInvoicesAccepted = getEstimatedInvoicesAccepted(apr, pipelineInvoiceCount);
  const estimatedPipelineSavings = getEstimatedPipelineSavings(apr, pipelineTotalAmount, daysEarly);
  return {
    apr,
    acceptanceRatePct,
    savingsPer10k,
    estimatedInvoicesAccepted,
    estimatedPipelineSavings,
  };
}
